import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useSuspenseQuery } from '@apollo/client/react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Position as PositionType } from '#src/__generated__/globalTypes.ts';
import { Position } from '#src/__generated__/globalTypes.ts';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BrandM, BrandXXL } from '#src/atoms/typography/index.tsx';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import { InstructionsContainer } from './InstructionsContainer/index.tsx';
import { Lineup } from './Lineup/index.tsx';
import { Subtitle } from './Subtitle/index.tsx';
import type {
  GetPlayerInfo,
  GetPlayerInfoVariables,
} from './__generated__/index.graphql.ts';
import bg from './assets/bg.webp';
import {
  INITIAL_SELECTED_PLAYERS,
  type LineupPosition,
  type SelectedPlayers,
} from './constants.ts';
import { createATeamData } from './data.ts';

const Root = styled(Vertical).attrs({
  gap: 4,
})`
  padding: var(--double-unit);
  background-image: url(${bg});
  background-position: left;
  background-size: contain;
  background-repeat: no-repeat;
  @media ${laptopAndAbove} {
    gap: calc(var(--unit) * 10);
    height: 100vh;
    padding-top: calc(var(--unit) * 8);
  }
`;

const LineupWrapper = styled(Vertical).attrs({ gap: 8 })`
  @media ${laptopAndAbove} {
    align-self: flex-start;
    justify-content: space-between;
  }
`;

const GameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--quadruple-unit);
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;

const positionDataMap = {
  [Position.Goalkeeper]: createATeamData.goalkeepers,
  [Position.Defender]: createATeamData.defenders,
  [Position.Midfielder]: createATeamData.midfielders,
  [Position.Forward]: createATeamData.forwards,
  [Position.Unknown]: createATeamData.extras,
} as const;

const getCurrentPosition = (
  selectedPlayers: SelectedPlayers
): LineupPosition | null => {
  const positions = [
    Position.Goalkeeper,
    Position.Defender,
    Position.Midfielder,
    Position.Forward,
    Position.Unknown,
  ] as const;

  for (const position of positions) {
    if (!selectedPlayers[position]) {
      return position;
    }
  }

  return null;
};

const GET_PLAYER_INFO = gql`
  query GetPlayerInfo(
    $slug: String!
    $cardSlug: String!
    $position: Position!
  ) {
    anyPlayer(slug: $slug) {
      slug
      ...InstructionsContainer_anyPlayer
      anySo5AverageLastScore(position: $position, span: LAST_FIFTEEN) {
        id
        percentageOfGamesPlayed
      }
    }
    anyCard(slug: $cardSlug) {
      slug
      pictureUrl
      power
      grade
    }
  }
  ${InstructionsContainer.fragments.anyPlayer}
` as TypedDocumentNode<GetPlayerInfo, GetPlayerInfoVariables>;

export const CreateATeam = () => {
  const [selectedCaptain, setSelectedCaptain] = useState<string | null>(null);
  const [selectedPlayers, setSelectedPlayers] = useState<SelectedPlayers>(
    () => INITIAL_SELECTED_PLAYERS
  );
  const isLaptopAndAbove = useIsLaptop();
  const currentPosition: LineupPosition | null = useMemo(
    () => getCurrentPosition(selectedPlayers),
    [selectedPlayers]
  );

  const currentPositionData = useMemo(() => {
    return positionDataMap[currentPosition as LineupPosition];
  }, [currentPosition]);

  const { data: firstPlayerInfo } = useSuspenseQuery(GET_PLAYER_INFO, {
    variables: {
      slug: currentPositionData?.player1.playerSlug || '',
      cardSlug: currentPositionData?.player1.cardSlug || '',
      position: (currentPosition || Position.Goalkeeper) as PositionType,
    },
    skip: !currentPositionData,
  });

  const { data: secondPlayerInfo } = useQuery(GET_PLAYER_INFO, {
    variables: {
      slug: currentPositionData?.player2.playerSlug || '',
      cardSlug: currentPositionData?.player2.cardSlug || '',
      position: currentPosition || Position.Goalkeeper,
    },
    skip: !currentPositionData,
  });

  const handleSetSelectedPlayers = useMemo(
    () => (players: { [key in PositionType]?: string | null }) => {
      setSelectedPlayers(prev => {
        const updated = { ...prev };
        for (const [key, value] of Object.entries(players)) {
          if (key in updated && value !== undefined) {
            updated[key as keyof SelectedPlayers] = value;
          }
        }
        return updated;
      });
    },
    []
  );

  return (
    <Root id="createATeam">
      <Vertical center={isLaptopAndAbove}>
        <BrandM uppercase color="var(--c-limited)">
          <FormattedMessage defaultMessage="How to play" />
        </BrandM>
        <BrandXXL uppercase>
          <FormattedMessage defaultMessage="Create a team" />
        </BrandXXL>
      </Vertical>
      <GameWrapper>
        <LineupWrapper>
          <Subtitle />
          <Lineup
            selectedPlayers={selectedPlayers}
            setSelectedCaptain={setSelectedCaptain}
            currentPositionData={currentPositionData}
            selectedCaptain={selectedCaptain}
            currentPosition={currentPosition}
          />
        </LineupWrapper>

        <InstructionsContainer
          setSelectedCaptain={setSelectedCaptain}
          currentPositionData={currentPositionData}
          currentPosition={currentPosition}
          selectedPlayers={selectedPlayers}
          setSelectedPlayers={setSelectedPlayers}
          handleSetSelectedPlayers={handleSetSelectedPlayers}
          firstPlayerInfo={firstPlayerInfo}
          secondPlayerInfo={secondPlayerInfo}
        />
      </GameWrapper>
    </Root>
  );
};
